import React, { useMemo } from 'react'
import { useLocation, useRouteMatch } from 'react-router'

import {
  Tooltip,
  Flex,
  WorkspaceLogo,
  useForTablet,
  LoadingPlaceholder,
} from '@sketch/components'

import {
  DropdownChevrons,
  DropdownContentContainer,
  DropdownDivider,
  DropdownLinkItem,
  DropdownTarget,
  SelectedWorkspaceName,
  SignedOutLabel,
  StyledSsoTag as SsoTag,
  WorkspaceName,
  StyledExpiredLabel as ExpiredLabel,
  DropdownHeader,
  DrodpwonItemSubtitle,
  DropdownInnerContainer,
  CreateWorkspaceButtonWrapper,
} from './WorkspacesDropdown.styles'

// Hooks
import {
  useGetInitialUserQuery,
  useGetUserSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { useGetWorkspaces } from 'modules/workspace/operations'
import useUserSignedIn from 'modules/user/useUserSignedIn'

import {
  SignInWithPasswordUser,
  useSignInToUnauthorizedWorkspace,
  useUserAuthorizations,
} from 'modules/sso/operations'
import { useIsNodeContentTruncated } from '@sketch/utils'
import { useResponsiveDropdown } from 'components/ResponsiveDropdown'

// Utils
import { routes, IS_EMBEDDED } from '@sketch/constants'
import {
  isPersonalWorkspace,
  getPersonalWorkspace,
  getMemberWorkspaces,
  getGuestWorkspaces,
} from 'modules/workspace/utils'

/**
 * TYPES
 */
export interface WorkspacesDropdownProps {
  workspace: WorkspaceMinimalFragment
}

interface WorkspaceDropdownItemProps {
  selectedWorkspace: WorkspaceMinimalFragment
  workspace: WorkspaceMinimalFragment
  user: SignInWithPasswordUser
}

const WorkspaceDropdownItem = ({
  workspace,
  selectedWorkspace,
  user,
}: WorkspaceDropdownItemProps) => {
  const location = useLocation()
  const isWorkspaceRoute = useRouteMatch({
    path: routes.WORKSPACE.template(),
  })
  const isWorkspaceProjectRoute = useRouteMatch({
    path: routes.WORKSPACE_PROJECT.template(),
  })

  const { hasAccessToWorkspace } = useUserAuthorizations()
  const { signIn } = useSignInToUnauthorizedWorkspace()
  const [
    isTextTruncated,
    truncatedNodeRef,
  ] = useIsNodeContentTruncated<HTMLDivElement>()
  const isTabletAndBigger = useForTablet()

  if (!workspace) {
    return null
  }

  const { identifier, avatar, name } = workspace
  const { ssoEnabled, ssoStartUrl } = workspace.customer || {}

  const isSelectedWorkspace = identifier === selectedWorkspace.identifier
  const isAuthorizedWorkspace = hasAccessToWorkspace(workspace)
  const isWorkspaceActive = workspace.status === 'ACTIVE'
  const isGuestWorkspace = workspace.userRole === 'GUEST'
  const isPartnerWorkspace = workspace.userRole === 'PARTNER'

  // If the device is too narrow, the tooltip doesn't fit in the screen
  // and looks broken.
  const shouldShowTooltip =
    isTabletAndBigger && (isPartnerWorkspace || isTextTruncated)

  let workspaceRoute
  if (workspace.userRole === 'FINANCE') {
    // Finance role user does not have access to documents, so it should
    // start with workspace settings
    workspaceRoute = routes.WORKSPACE_SETTINGS_PEOPLE.create({
      workspaceId: identifier,
    })
  } else {
    // Mantain the pathname when changing between workspaces routes
    if (isWorkspaceRoute && !isWorkspaceProjectRoute) {
      workspaceRoute = location.pathname.replace(
        selectedWorkspace.identifier,
        identifier
      )
    } else {
      workspaceRoute = routes.WORKSPACE_SHARES.create({
        workspaceId: identifier,
      })
    }
  }

  return (
    <Tooltip
      content={isPartnerWorkspace ? identifier : name}
      placement="right"
      disabled={!shouldShowTooltip}
    >
      <DropdownLinkItem
        onClick={event => {
          // If is authorized then we use the `to` route
          if (isAuthorizedWorkspace) return

          event.preventDefault()
          const redirectBackToCurrentUrl = false
          signIn({
            workspace: {
              workspaceId: identifier,
              workspaceName: name,
              ssoEnabled,
              ssoStartUrl: ssoStartUrl || undefined,
            },
            user: { ...user, isGuestWorkspace },
            redirectBackToCurrentUrl,
          })
        }}
        to={workspaceRoute}
        isActive={() => isSelectedWorkspace}
        $expired={!isWorkspaceActive}
      >
        {isPersonalWorkspace(workspace) ? (
          <WorkspaceLogo.Icon icon="briefcase" size="24px" UNSAFE_ADD_MARGIN />
        ) : (
          <WorkspaceLogo
            workspaceName={name}
            src={avatar?.large}
            size="24px"
            UNSAFE_ADD_MARGIN
          />
        )}

        <WorkspaceName
          ref={truncatedNodeRef}
          $isSignedOutLabelVisible={!isAuthorizedWorkspace}
        >
          {name}
        </WorkspaceName>
        {!isWorkspaceActive && <ExpiredLabel />}
        {/* Guests and Partners users don't have access to SSO information */}
        {ssoEnabled && !isGuestWorkspace && !isPartnerWorkspace && <SsoTag />}
        {!isAuthorizedWorkspace && (
          <SignedOutLabel expired={!isWorkspaceActive} />
        )}
      </DropdownLinkItem>
    </Tooltip>
  )
}

const WorkspaceDropdownContent = ({ workspace }: WorkspacesDropdownProps) => {
  const { data } = useGetInitialUserQuery()
  const { data: userSettingsData } = useGetUserSettingsQuery()
  const isSignedIn = useUserSignedIn()

  const { workspaces, loading: workspacesLoading } = useGetWorkspaces({
    skip: !isSignedIn || IS_EMBEDDED,
  })

  const hasPersonalIdentity = data?.me.hasPersonalIdentity

  const getMembershipUser = (
    workspaceIdentifier: string
  ): SignInWithPasswordUser => {
    const workspaceMembership = userSettingsData?.me.workspaceMemberships.entries.find(
      membership => membership.workspace.identifier === workspaceIdentifier
    )

    return {
      name: data?.me?.name ?? '',
      email: workspaceMembership?.user?.email ?? '',
      avatarSrc: data?.me?.avatar?.small ?? '',
    }
  }

  const [personalWorkspace, memberWorkspaces, guestWorkspaces] = useMemo(() => {
    const personalWorkspace = getPersonalWorkspace(workspaces)
    const memberWorkspaces = getMemberWorkspaces(workspaces)
    const guestWorkspaces = getGuestWorkspaces(workspaces)

    return [personalWorkspace, memberWorkspaces, guestWorkspaces] as const
  }, [workspaces])

  return (
    <DropdownContentContainer data-testid="workspace-dropdown">
      {/* Personal workspace */}
      {personalWorkspace && (
        <>
          <WorkspaceDropdownItem
            key={personalWorkspace.identifier}
            selectedWorkspace={workspace}
            workspace={personalWorkspace}
            user={getMembershipUser(personalWorkspace.identifier)}
          />
          <DropdownDivider />
        </>
      )}

      {/* Member workspaces */}
      {memberWorkspaces.length > 0 && (
        <DropdownHeader>Member of</DropdownHeader>
      )}
      {memberWorkspaces.map(workspaceItem => (
        <WorkspaceDropdownItem
          key={workspaceItem.identifier}
          selectedWorkspace={workspace}
          workspace={workspaceItem}
          user={getMembershipUser(workspaceItem.identifier)}
        />
      ))}

      {memberWorkspaces.length > 0 && guestWorkspaces.length > 0 && (
        <DropdownDivider />
      )}

      {/* Guest workspaces */}
      {guestWorkspaces.length > 0 && <DropdownHeader>Guest in</DropdownHeader>}
      {guestWorkspaces.map(workspaceItem => (
        <WorkspaceDropdownItem
          key={workspaceItem.identifier}
          selectedWorkspace={workspace}
          workspace={workspaceItem}
          user={getMembershipUser(workspaceItem.identifier)}
        />
      ))}

      {workspacesLoading && (
        <Flex justifyContent="center" flex="auto">
          <LoadingPlaceholder size="24px" />
        </Flex>
      )}

      {/* Workspace creation */}
      {hasPersonalIdentity && !data.me.isPartner && (
        <CreateWorkspaceButtonWrapper>
          <DropdownDivider />
          <DropdownLinkItem to={routes.WORKSPACE_CREATE.create({})}>
            <WorkspaceLogo.Icon size="24px" icon="plus" UNSAFE_ADD_MARGIN />
            <div>
              Create Workspace
              <DrodpwonItemSubtitle>
                30-Day trial available
              </DrodpwonItemSubtitle>
            </div>
          </DropdownLinkItem>
        </CreateWorkspaceButtonWrapper>
      )}
    </DropdownContentContainer>
  )
}

/**
 * MAIN COMPONENT
 */
export const WorkspacesDropdown = ({ workspace }: WorkspacesDropdownProps) => {
  const [responsiveWorkspaceDropdown, buttonProps] = useResponsiveDropdown({
    dropdown: WorkspaceDropdownContent,
    dropdownProps: {
      workspace,
    },
    usePortal: true,
    placement: 'bottom',
    dropdownStyle: {
      padding: 0,
    },
  })

  return (
    <>
      <DropdownTarget {...buttonProps}>
        <DropdownInnerContainer>
          {isPersonalWorkspace(workspace) ? (
            <WorkspaceLogo.Icon
              icon="briefcase"
              size="24px"
              UNSAFE_ADD_MARGIN
            />
          ) : (
            <WorkspaceLogo
              workspaceName={workspace.name}
              src={workspace.avatar?.large}
              size="24px"
              UNSAFE_ADD_MARGIN
            />
          )}
          <SelectedWorkspaceName>{workspace.name}</SelectedWorkspaceName>
          <DropdownChevrons />
        </DropdownInnerContainer>
      </DropdownTarget>
      {responsiveWorkspaceDropdown}
    </>
  )
}
